import { GodRays } from '@paper-design/shaders-react'
import { useEffect, useId, useMemo, useState } from 'react'
import { Platform, View } from 'react-native'
import { cn } from '~/lib/utils'

export default function GodRaysBackground({
  className,
  backgroundColor,
  colors,
  colorBloom,
  bloom = 1,
  offsetX,
  offsetY,
  midSize = 1.5,
  midIntensity = 1.2,
  speed = 0.75,
  intensity = 0.25,
  density = 0.5,
  animateOnlyWhenInView: _animateOnlyWhenInView = true,
}: {
  className?: string
  backgroundColor?: string
  colors?: string[]
  colorBloom?: string
  bloom?: number
  offsetX?: number
  offsetY?: number
  midSize?: number
  midIntensity?: number
  speed?: number
  intensity?: number
  density?: number
  animateOnlyWhenInView?: boolean
}) {
  const reactDomId = useId()
  const visibilityRootDomId = useMemo(
    () => `godrays-visibility-root-${reactDomId.replaceAll(':', '')}`,
    [reactDomId]
  )

  const animateOnlyWhenInView = _animateOnlyWhenInView && Platform.OS === 'web'
  const [isIntersecting, setIsIntersecting] = useState(!animateOnlyWhenInView)

  useEffect(() => {
    if (!animateOnlyWhenInView) {
      return
    }

    const rootElement = document.getElementById(visibilityRootDomId)

    if (!rootElement) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0]

        if (firstEntry) {
          setIsIntersecting(firstEntry.isIntersecting)
        }
      },
      { root: null, rootMargin: '50px 0px', threshold: 0 }
    )

    observer.observe(rootElement)

    return () => {
      observer.disconnect()
    }
  }, [animateOnlyWhenInView, visibilityRootDomId])

  const godRays = (
    <GodRays
      className={cn('absolute! inset-0 z-0 transform-gpu', className)}
      colorBack={backgroundColor}
      colors={colors}
      offsetX={offsetX}
      offsetY={offsetY}
      midSize={midSize}
      midIntensity={midIntensity}
      density={density}
      intensity={intensity}
      speed={isIntersecting ? speed : 0}
      fit="cover"
      colorBloom={colorBloom}
      bloom={bloom}
    />
  )

  if (animateOnlyWhenInView) {
    return (
      <View
        nativeID={visibilityRootDomId}
        collapsable={false}
        className="pointer-events-none absolute inset-0 z-0">
        {godRays}
      </View>
    )
  }

  return godRays
}
